import { formatCodeableConcept } from '@medplum/core'
import type {
  Address,
  ContactPoint,
  Patient,
  Resource,
} from '@medplum/fhirtypes'
import { sortBy, uniq } from 'lodash-es'

export function getMaritalStatus(patient: Patient): string | undefined {
  const maritalStatus = patient.maritalStatus
    ? formatCodeableConcept(patient.maritalStatus)
    : 'Unknown'
  return maritalStatus
}

export function getPrimaryPhoneNumber(
  resource: { telecom?: ContactPoint[] | undefined } | undefined | null
): string | undefined {
  return getPrimaryContactMethod(resource?.telecom, 'phone')
}

export function getPrimaryFaxNumber(
  resource: { telecom?: ContactPoint[] | undefined } | undefined | null
): string | undefined {
  return getPrimaryContactMethod(resource?.telecom, 'fax')
}

export function getPrimaryEmail(
  resource: { telecom?: ContactPoint[] | undefined } | undefined | null
): string | undefined {
  return getPrimaryContactMethod(resource?.telecom, 'email')
}

// All fax numbers on file, not just the primary — a provider can have more
// than one fax destination (e.g. a main line and a records department).
export function getFaxNumbers(
  resource: { telecom?: ContactPoint[] | undefined } | undefined | null
): string[] {
  const faxPoints = sortBy(
    (resource?.telecom ?? []).filter(
      (t) => t.system === 'fax' && t.value !== undefined
    ),
    'rank'
  )
  return uniq(faxPoints.map((t) => t.value as string))
}

function getPrimaryContactMethod(
  contactPoints: ContactPoint[] | undefined,
  system: ContactPoint['system']
): string | undefined {
  const matchingCPs = sortBy(
    (contactPoints ?? []).filter(
      (t) => t.system === system && t.value !== undefined
    ),
    'rank'
  )
  return matchingCPs[0]?.value
}

export function homeAddressOrFallback(
  resource: { address?: Address[] | undefined } | undefined | null
): Address | undefined {
  return (
    resource?.address?.find((address) => address.use === 'home') ??
    resource?.address?.[0]
  )
}

export function isInstance(resource: Resource): resource is Patient {
  return resource.resourceType === 'Patient'
}
